import React, { useMemo } from 'react';
import useGlobals from '../../../../../../Hooks/useGlobals';
import StatusBadge from './StatusBadge';
import TwoLineSelect from '../../McpTab/components/TwoLineSelect';

interface ConfigurationSectionProps {
    configs: Array<{ identifier: string; title: string }>;
    selectedConfig: { identifier: string; title: string } | null;
    endpoints: Array<{
        identifier: string;
        method: string;
        path: string;
        name: string;
        fullUrl: string;
        description?: string;
    }>;
    selectedEndpoint: {
        identifier: string;
        method: string;
        path: string;
        name: string;
        fullUrl: string;
        description?: string;
    } | null;
    isLoading: boolean;
    onConfigChange: (configId: string) => void;
    onEndpointChange: (endpointId: string) => void;
}

const ConfigurationSection: React.FC<ConfigurationSectionProps> = ({
    configs,
    selectedConfig,
    endpoints,
    selectedEndpoint,
    isLoading,
    onConfigChange,
    onEndpointChange,
}) => {
    const { i18n } = useGlobals();

    const items = useMemo(() => {
        const sorted = [...endpoints].sort((a, b) => {
            const m = a.method.localeCompare(b.method);
            if (m !== 0) return m;
            return a.name.localeCompare(b.name);
        });
        return sorted.map((endpoint) => ({
            value: endpoint.identifier,
            label: `${endpoint.method} ${endpoint.path} - ${endpoint.name}`,
            subLabel: endpoint.description,
        }));
    }, [endpoints]);

    return (
        <>
            {selectedConfig ? (
                <div>
                    <div className="my-4">
                        <h4>{i18n.translate({ id: 'testing.setup.title', sourceName: 'OpenAPI' })}</h4>
                    </div>

                    {/* Endpoint Selection */}
                    <label className="flex flex-col gap-2 text-sm">
                        {i18n.translate({ id: 'testing.endpoint.label', sourceName: 'OpenAPI' })}

                        <TwoLineSelect
                            items={items}
                            value={selectedEndpoint?.identifier || ''}
                            onChange={onEndpointChange}
                            disabled={!selectedConfig || endpoints.length === 0}
                            placeholder={i18n.translate({ id: 'testing.endpoint.placeholder', sourceName: 'OpenAPI' })}
                        />
                    </label>

                    {selectedEndpoint && (
                        <div className="mt-6 py-4 bg-layout-backgroundColor/30 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                                <StatusBadge type="method" value={selectedEndpoint.method} />
                                <code className="text-sm bg-config-boxBackgroundColor px-2 py-1 rounded border border-layout-borderColor text-layout-textcolor">
                                    {selectedEndpoint.fullUrl}
                                </code>
                            </div>
                            {selectedEndpoint.description && (
                                <span className="text-sm text-gray-500">{selectedEndpoint.description}</span>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <></>
            )}
        </>
    );
};

export default ConfigurationSection;
